import React from 'react';
import { Content } from 'mdast';

// Rendered by the `:::introLinks` container directive (see Markdown.tsx).
// A grid of quick-link cards for the end of the intro — "Listen", "Summary",
// "Read as PDF", "About & FAQs" and the like. The cards are authored in the
// Google Doc as a bulleted list, one card per bullet:
//
//     :::introLinks
//     * Listen to the story: [Spotify](url) · [Apple](url) · [Web](url)
//     * [Summary](/summary)
//     * [Read as PDF](/scenario.pdf)
//     * [About & FAQs](/supplements/faq)
//     :::
//
// A bullet that is a single link becomes a card linking there, labeled with
// the link text. A bullet with leading text and one or more links becomes a
// card labeled with the leading text (trailing ":" dropped); the whole card
// opens the first link and each link also renders in a small sub-row
// ("Spotify · Apple · Web"). Plain text between links (e.g. "·") is ignored —
// the sub-row adds its own separators. Icons are keyword-matched from the
// card label (see pickIcon).

type IntroLinkCard = {
  label: string;
  primaryUrl: string;
  subLinks: { text: string; url: string }[];
};

// Inline text of a node, links included.
function textOf(node: any): string {
  if (!node) return '';
  if (typeof node.value === 'string') return node.value;
  if (Array.isArray(node.children)) return node.children.map(textOf).join('');
  return '';
}

// Walk an entry's inline nodes: collect links in order, and text before the
// first link (recursing so a bolded label or a link inside emphasis still
// counts). Text after the first link is separator noise and is dropped.
function collectInline(
  nodes: any[],
  acc: { leading: string[]; links: { text: string; url: string }[] },
) {
  for (const node of nodes) {
    if (!node) continue;
    if (node.type === 'link') {
      acc.links.push({ text: textOf(node).trim(), url: node.url });
    } else if (Array.isArray(node.children)) {
      collectInline(node.children, acc);
    } else if (typeof node.value === 'string' && acc.links.length === 0) {
      acc.leading.push(node.value);
    }
  }
}

function parseIntroLinkCards(children: Content[]): IntroLinkCard[] {
  // One entry per list item; gdoc spacing sometimes turns list items into
  // bare paragraphs (see the `:::table` handler), so accept those too.
  const entries: any[] = [];
  for (const child of children as any[]) {
    if (child.type === 'list') entries.push(...(child.children ?? []));
    else if (child.type === 'paragraph') entries.push(child);
  }

  const cards: IntroLinkCard[] = [];
  for (const entry of entries) {
    const inline =
      entry.type === 'listItem'
        ? (entry.children ?? []).flatMap((p: any) => p.children ?? [])
        : (entry.children ?? []);
    const acc = { leading: [] as string[], links: [] as { text: string; url: string }[] };
    collectInline(inline, acc);
    if (acc.links.length === 0) continue;

    const leading = acc.leading.join('').trim().replace(/[:–—-]\s*$/, '').trim();
    if (leading) {
      cards.push({ label: leading, primaryUrl: acc.links[0].url, subLinks: acc.links });
    } else {
      cards.push({
        label: acc.links[0].text,
        primaryUrl: acc.links[0].url,
        subLinks: acc.links.slice(1),
      });
    }
  }
  return cards;
}

const iconProps = {
  className: 'h-[22px] w-[22px]',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

const HeadphonesIcon = () => (
  <svg {...iconProps}>
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
  </svg>
);

const ListIcon = () => (
  <svg {...iconProps}>
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const DownloadIcon = () => (
  <svg {...iconProps}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const InfoIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const ArrowIcon = () => (
  <svg {...iconProps}>
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="7 7 17 7 17 17" />
  </svg>
);

function pickIcon(label: string): () => React.JSX.Element {
  const l = label.toLowerCase();
  if (/listen|audio|podcast|narrat/.test(l)) return HeadphonesIcon;
  if (/summar|overview|contents|glance/.test(l)) return ListIcon;
  if (/pdf|download|print/.test(l)) return DownloadIcon;
  if (/about|faq|info|question/.test(l)) return InfoIcon;
  return ArrowIcon;
}

const externalLinkProps = (url: string) =>
  url.startsWith('/') || url.startsWith('#')
    ? {}
    : { target: '_blank', rel: 'noopener noreferrer' };

export default function IntroLinkCards({ nodes }: { nodes: Content[] }) {
  const cards = parseIntroLinkCards(nodes);
  if (cards.length === 0) return null;

  return (
    <div className="my-8 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
      {cards.map((card, index) => {
        const Icon = pickIcon(card.label);
        return (
          <div
            key={`${index}-${card.label}`}
            className="group relative flex aspect-square flex-col justify-between gap-3 rounded-md border-[1.5px] border-black p-4 transition-colors duration-150 hover:bg-black hover:text-white"
          >
            <Icon />
            <div>
              {/* Font size + leading live on these block wrappers, not just the
                  inline <a>s: the line boxes are sized by the block's strut,
                  which would otherwise inherit .good-ending-text's 1.4rem and
                  double-space the 11px label. */}
              <div className="font-mono text-[11px] leading-[1.55] [text-wrap:balance]">
                {/* Stretched link: the ::before overlay makes the whole card open
                    the primary destination; sub-links sit above it (z-10). */}
                <a
                  href={card.primaryUrl}
                  {...externalLinkProps(card.primaryUrl)}
                  className="font-semibold uppercase tracking-[0.12em] text-inherit no-underline before:absolute before:inset-0 before:content-['']"
                >
                  {card.label}
                </a>
              </div>
              {card.subLinks.length > 0 && (
                <div className="pointer-events-none relative z-10 mt-1 font-mono text-[11px] leading-[1.6] opacity-70">
                  {card.subLinks.map((subLink, subIndex) => (
                    <span key={`${subIndex}-${subLink.url}`}>
                      <a
                        href={subLink.url}
                        {...externalLinkProps(subLink.url)}
                        className="pointer-events-auto whitespace-nowrap text-inherit no-underline opacity-80 hover:underline hover:opacity-100"
                      >
                        {subLink.text}
                      </a>
                      {subIndex < card.subLinks.length - 1 && (
                        <span className="opacity-50" aria-hidden="true">{' · '}</span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
